import React, { useCallback, useEffect, useRef, useState } from 'react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import adsApi from './adsApi';

export default function AdsReportsPage() {
  const [tab, setTab] = useState('campaigns');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [heatmapCampaignId, setHeatmapCampaignId] = useState('');
  const [heatmapPoints, setHeatmapPoints] = useState([]);
  const [revenueForm, setRevenueForm] = useState({ campaign_id: '', amount: '', notes: '' });
  const canvasRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let res;
      if (tab === 'campaigns') res = await adsApi.campaignReport();
      else if (tab === 'advertisers') res = await adsApi.advertiserReport();
      else if (tab === 'revenue') res = await adsApi.revenueReport();
      else if (tab === 'cities') res = await adsApi.citiesReport();
      else if (tab === 'devices') res = await adsApi.devicesReport();
      else res = await adsApi.topReport();
      setRows(res?.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !heatmapPoints.length) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'rgba(15, 23, 42, 0.04)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    for (const p of heatmapPoints) {
      const x = (Number(p.x) / 100) * canvas.width;
      const y = (Number(p.y) / 100) * canvas.height;
      const grad = ctx.createRadialGradient(x, y, 0, x, y, 18);
      grad.addColorStop(0, 'rgba(239, 68, 68, 0.55)');
      grad.addColorStop(1, 'rgba(239, 68, 68, 0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, 18, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [heatmapPoints]);

  const loadHeatmap = async () => {
    if (!heatmapCampaignId) return;
    try {
      const res = await adsApi.heatmap(heatmapCampaignId);
      setHeatmapPoints(res?.data || []);
    } catch (err) {
      setError(err.message || 'Heatmap failed');
    }
  };

  const exportReport = (format) => {
    const type =
      tab === 'top'
        ? 'top'
        : tab === 'cities'
          ? 'cities'
          : tab === 'devices'
            ? 'devices'
            : tab === 'advertisers'
              ? 'advertiser'
              : tab === 'revenue'
                ? 'revenue'
                : 'campaign';
    window.open(adsApi.exportUrl(format, type), '_blank');
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Ads Reports</h1>
        <div className="flex gap-2">
          <button type="button" className="rounded border border-slate-300 px-3 py-2 text-sm" onClick={() => exportReport('csv')}>
            Export Excel/CSV
          </button>
          <button type="button" className="rounded border border-slate-300 px-3 py-2 text-sm" onClick={() => exportReport('pdf')}>
            Export PDF
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {['campaigns', 'advertisers', 'revenue', 'top', 'cities', 'devices', 'heatmap'].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-md px-3 py-1.5 text-sm ${tab === t ? 'bg-slate-900 text-white' : 'border border-slate-300 bg-white'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {tab === 'heatmap' ? (
        <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex gap-2">
            <input
              className="rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Campaign ID"
              value={heatmapCampaignId}
              onChange={(e) => setHeatmapCampaignId(e.target.value)}
            />
            <button type="button" className="rounded bg-slate-900 px-3 py-2 text-sm text-white" onClick={loadHeatmap}>
              Load heatmap
            </button>
          </div>
          <canvas ref={canvasRef} width={800} height={320} className="w-full rounded border border-slate-200" />
          <p className="text-xs text-slate-500">{heatmapPoints.length} click points</p>
        </div>
      ) : loading ? (
        <LoadingSpinner size="large" message="Loading report..." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {Object.keys(rows[0] || { empty: '' }).map((k) => (
                  <th key={k} className="px-3 py-2">
                    {k}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  {Object.values(row).map((v, i) => (
                    <td key={i} className="px-3 py-2">
                      {v == null ? '—' : String(v)}
                    </td>
                  ))}
                </tr>
              ))}
              {!rows.length ? (
                <tr>
                  <td className="px-3 py-6 text-center text-slate-500">No data</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'revenue' ? (
        <form
          className="flex flex-wrap gap-2 rounded-lg border border-slate-200 bg-white p-4"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await adsApi.addRevenue({
                campaign_id: Number(revenueForm.campaign_id),
                amount: Number(revenueForm.amount),
                notes: revenueForm.notes,
                source: 'manual',
              });
              setRevenueForm({ campaign_id: '', amount: '', notes: '' });
              await load();
            } catch (err) {
              setError(err.message || 'Failed to add revenue');
            }
          }}
        >
          <input
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="Campaign ID"
            value={revenueForm.campaign_id}
            onChange={(e) => setRevenueForm((f) => ({ ...f, campaign_id: e.target.value }))}
            required
          />
          <input
            type="number"
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="Amount"
            value={revenueForm.amount}
            onChange={(e) => setRevenueForm((f) => ({ ...f, amount: e.target.value }))}
            required
          />
          <input
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="Notes"
            value={revenueForm.notes}
            onChange={(e) => setRevenueForm((f) => ({ ...f, notes: e.target.value }))}
          />
          <button type="submit" className="rounded bg-slate-900 px-3 py-2 text-sm text-white">
            Add revenue entry
          </button>
        </form>
      ) : null}
    </div>
  );
}
